import { RouteLocation, RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n, { loadTranslations } from '@/plugins/i18n';

const Warehouse = () => import('@/pages/Warehouse/index.vue');
const WarehouseASNImportView = () => import('@/pages/Warehouse/ASNImport/ASNImport.vue');
const WarehouseOutboundQueueView = () => import('@/pages/Warehouse/OutboundQueue/OutboundQueue.vue');
const WarehouseShipmentsView = () => import('@/pages/Warehouse/Shipments/WarehouseShipments.vue');
const WarehouseFulfillmentQueueView = () => import('@/pages/Warehouse/FulfillmentQueue/FulfillmentQueue.vue');
const WarehouseReplenishmentQueueView = () => import('@/pages/Warehouse/ReplenishmentQueue/ReplenishmentQueue.vue');

async function beforeEnter() {
  await Promise.all([loadTranslations('warehouse'), loadTranslations('logistics')]);
}

export function warehouseTabRoutes(): Readonly<RouteTab[]> {
  const routes = [
    {
      to: { name: ROUTE_NAMES.OUTBOUND_QUEUE },
      label: i18n.t('warehouse.tabs.outboundQueue'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessBrandFulfillment;
      },
    },
    {
      to: { name: ROUTE_NAMES.SHIPMENTS },
      label: i18n.t('warehouse.tabs.shipments'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessBrandFulfillment;
      },
    },
    {
      to: { name: ROUTE_NAMES.WAREHOUSE_FULFILLMENT_QUEUE },
      label: i18n.t('warehouse.tabs.brandFulfillmentQueue'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessWarehouseBrandFulfillment;
      },
    },
    {
      to: { name: ROUTE_NAMES.REPLENISHMENT_QUEUE },
      label: i18n.t('warehouse.tabs.replenishmentQueue'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessR11TFulfillment;
      },
    },
    {
      to: { name: ROUTE_NAMES.ASN_IMPORT },
      label: i18n.t('warehouse.tabs.asnImport'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canManageWhsAsnCreation;
      },
    },
  ];

  return Object.freeze(routes);
}

const warehouseRouteRedirects = [
  {
    rules: ['canAccessBrandFulfillment'],
    routeName: ROUTE_NAMES.OUTBOUND_QUEUE,
  },
  {
    rules: ['canAccessWarehouseBrandFulfillment'],
    routeName: ROUTE_NAMES.WAREHOUSE_FULFILLMENT_QUEUE,
  },
  {
    rules: ['canAccessR11TFulfillment'],
    routeName: ROUTE_NAMES.REPLENISHMENT_QUEUE,
  },
  {
    rules: ['canManageWhsAsnCreation'],
    routeName: ROUTE_NAMES.ASN_IMPORT,
  },
];

export function getWarehouseRouteNameFromRedirects(): string {
  const userStore = useUserStore();

  const permissions = {
    ...userStore.userActiveCompanyPermissions,
    ...userStore.user.featureFlags,
  };

  const firstPermittedRedirect = warehouseRouteRedirects.find(({ rules }) => {
    return rules.every((rule) => permissions[rule]);
  });

  return firstPermittedRedirect?.routeName || ROUTE_NAMES.ERROR_403;
}

const warehouseRoutes: RouteRecordRaw[] = [
  {
    path: 'warehouse/',
    component: Warehouse,
    beforeEnter,
    meta: {
      tabs: warehouseTabRoutes,
      title: () => i18n.t('warehouse.brandFulfillment.title'),
      description: () => i18n.t('warehouse.brandFulfillment.description'),
    },
    children: [
      {
        path: '',
        component: Warehouse,
        name: ROUTE_NAMES.WAREHOUSE,
        meta: {
          title: () => i18n.t('warehouse.brandFulfillment.title'),
          description: () => i18n.t('warehouse.brandFulfillment.description'),
        },
        beforeEnter(to: RouteLocation) {
          return { name: getWarehouseRouteNameFromRedirects(), params: { company: to.params.company } };
        },
      },
      {
        path: 'asn-import',
        name: ROUTE_NAMES.ASN_IMPORT,
        component: WarehouseASNImportView,
        meta: {
          title: () => i18n.t('warehouse.brandFulfillment.title'),
          description: () => i18n.t('warehouse.brandFulfillment.description'),
          permissions: [
            {
              rules: ['canManageWhsAsnCreation'],
            },
          ],
        },
      },
      {
        path: 'outbound-queue',
        name: ROUTE_NAMES.OUTBOUND_QUEUE,
        component: WarehouseOutboundQueueView,
        meta: {
          title: () => i18n.t('warehouse.brandFulfillment.title'),
          description: () => i18n.t('warehouse.brandFulfillment.description'),
          permissions: [
            {
              rules: ['canAccessBrandFulfillment'],
            },
          ],
        },
      },
      {
        path: 'shipments',
        name: ROUTE_NAMES.SHIPMENTS,
        component: WarehouseShipmentsView,
        meta: {
          title: () => i18n.t('warehouse.brandFulfillment.title'),
          description: () => i18n.t('warehouse.brandFulfillment.description'),
          permissions: [
            {
              rules: ['canAccessBrandFulfillment'],
            },
          ],
        },
      },
      {
        path: 'fulfillment-queue',
        name: ROUTE_NAMES.WAREHOUSE_FULFILLMENT_QUEUE,
        component: WarehouseFulfillmentQueueView,
        meta: {
          title: () => i18n.t('warehouse.brandFulfillment.title'),
          description: () => i18n.t('warehouse.brandFulfillment.description'),
          permissions: [
            {
              rules: ['canAccessWhsFulfillment'],
            },
          ],
        },
      },
      {
        path: 'replenishment-queue',
        name: ROUTE_NAMES.REPLENISHMENT_QUEUE,
        component: WarehouseReplenishmentQueueView,
        meta: {
          title: () => i18n.t('warehouse.brandFulfillment.title'),
          description: () => i18n.t('warehouse.brandFulfillment.description'),
          permissions: [
            {
              rules: ['canAccessR11TFulfillment'],
            },
          ],
        },
      },
      {
        path: ':catchAll(.*)*',
        name: ROUTE_NAMES.WAREHOUSE_WILDCARD,
        redirect: { name: ROUTE_NAMES.ERROR_404 },
      },
    ],
  },
];

export default warehouseRoutes;
